import { useMutation } from "@apollo/client";
import moment from "moment";
import { FormEvent, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  CreateOrUpdateKpiMeasurementMutationMutation,
  CreateOrUpdateKpiMeasurementMutationMutationVariables,
} from "types/graphql-schema";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import Loading from "@components/loading/loading";

import createOrUpdateKpiMeasurementMutation from "../graphql/create-or-update-kpi-measurement-mutation";
import getKpiQuery from "../graphql/get-kpi-query";

const KPIAddMeasurementForm = ({
  kpi,
  onCreated,
}: {
  kpi: {
    id: number;
  };
  onCreated: () => void;
}) => {
  const [formValidationError, setFormValidationError] = useState("");
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const urlDate = searchParams.has("date")
    ? moment(searchParams.get("date"))
        .hours(moment().hours())
        .minutes(moment().minutes())
    : moment();
  const [form, setForm] = useState<{
    measurement: string;
    datetime: Date | null;
  }>({
    measurement: "0",
    datetime: urlDate.toDate(),
  });
  const [createKpiMeasurement, { loading }] = useMutation<
    CreateOrUpdateKpiMeasurementMutationMutation,
    CreateOrUpdateKpiMeasurementMutationMutationVariables
  >(createOrUpdateKpiMeasurementMutation);

  const handleSubmitForm = (e: FormEvent) => {
    e.preventDefault();
    if (Number.isNaN(form.measurement)) {
      setFormValidationError("Measurement value has to be a valid number");
      return;
    }
    if (!form.datetime) {
      setFormValidationError("Please enter a valid date");
      return;
    }
    if (moment(form.datetime).isAfter(moment().add(1, "minute"))) {
      setFormValidationError("Please enter a date that is not in the future.");
      return;
    }

    createKpiMeasurement({
      variables: {
        measurement: Number(form.measurement),
        timestamp: moment(form.datetime).format(),
        kpiId: kpi.id,
      },

      refetchQueries: [getKpiQuery],
      onCompleted: onCreated,
    });
  };

  // RENDER
  return (
    <form
      className="flex flex-col gap-6 border rounded-lg mb-6 p-6 bg-gray-50"
      onSubmit={handleSubmitForm}
    >
      <div className="max-w-96">
        <Input
          label="Measurement value"
          value={form.measurement}
          onChange={(e) =>
            setForm({
              ...form,
              measurement: e.target.value,
            })
          }
        />
      </div>
      <div className="max-w-96">
        <InputDatePicker
          withPortal
          date={form.datetime}
          onChangeDate={(datetime) => setForm({ ...form, datetime })}
          dateFormat="MM/dd/yyyy h:mm aa"
          maxDate={new Date()}
          showTimeInput
          placeholderText="Time stamp"
        />
      </div>
      {formValidationError && (
        <div className="text-sm text-red-700">{formValidationError}</div>
      )}
      <div className="flex gap-2">
        <Button
          disabled={loading}
          text="Add measurement"
          theme={buttonTheme.primary}
          type="submit"
        />
        {loading && <Loading mini size="5" />}
      </div>
    </form>
  );
};

export default KPIAddMeasurementForm;
